/**
 * 名人案例数据，逐条来自旧站产物（硬编码，不走后端）。
 *
 * `vipOnly` 为 true 的条目在旧站是会员可见；这里保持同样的口径，
 * 未开通会员时只显示姓名与朝代，不展示干支。
 *
 * 措辞注意：本页只陈列干支资料本身，不对人物命运做任何判断性描述。
 */
export type FamousCase = {
  name: string;
  sex: string;
  category: string;
  note: string;
  /** 天干四字，年月日时 */
  tg: string;
  /** 地支四字，年月日时 */
  dz: string;
  vipOnly: boolean;
  /** URL 短标识（拼音），用于独立可收录的详情页 /famous/<slug> */
  slug: string;
};

export const famousCases: FamousCase[] = [
  { name: "忽必烈", sex: "男", category: "君主", note: "元朝 开国皇帝", tg: "乙乙乙乙", dz: "亥酉酉酉", vipOnly: false, slug: "hubilie" },
  { name: "元顺帝", sex: "男", category: "君主", note: "元朝 末位皇帝", tg: "丁壬乙丙", dz: "未子未子", vipOnly: false, slug: "yuanshundi" },
  { name: "朱元璋", sex: "男", category: "君主", note: "明朝 开国皇帝", tg: "戊壬丁丁", dz: "辰戌丑未", vipOnly: false, slug: "zhuyuanzhang" },
  { name: "王阳明", sex: "男", category: "历史名人", note: "明朝 哲学家", tg: "壬辛癸癸", dz: "辰亥亥亥", vipOnly: false, slug: "wangyangming" },
  { name: "琼瑶", sex: "女", category: "文学", note: "现代 作家", tg: "戊丙壬癸", dz: "寅辰午卯", vipOnly: false, slug: "qiongyao" },
  { name: "文潞公", sex: "男", category: "文学", note: "北宋 文学家", tg: "癸乙庚丙", dz: "亥丑戌子", vipOnly: false, slug: "wenlugong" },
  { name: "老舍", sex: "男", category: "文学", note: "现代 作家", tg: "戊乙壬己", dz: "戌丑寅酉", vipOnly: false, slug: "laoshe" },
  { name: "明建文帝", sex: "男", category: "君主", note: "明朝 第二位皇帝", tg: "丁壬己癸", dz: "巳子卯酉", vipOnly: true, slug: "jianwendi" },
  { name: "朱棣", sex: "男", category: "君主", note: "明朝 第三位皇帝", tg: "庚辛癸辛", dz: "子巳酉酉", vipOnly: true, slug: "zhudi" },
  { name: "孔子", sex: "男", category: "历史名人", note: "春秋 儒家创始人", tg: "庚戊庚甲", dz: "戌子子申", vipOnly: true, slug: "kongzi" },
  { name: "胡雪岩", sex: "男", category: "商界", note: "晚清 商人", tg: "癸癸己丁", dz: "酉亥巳卯", vipOnly: true, slug: "huxueyan" },
  { name: "梅兰芳", sex: "男", category: "娱乐", note: "京剧 表演艺术家", tg: "甲丙甲庚", dz: "午子寅午", vipOnly: true, slug: "meilanfang" },
  { name: "弘一法师", sex: "男", category: "僧道", note: "近代 高僧", tg: "庚乙丁壬", dz: "辰酉亥寅", vipOnly: true, slug: "hongyifashi" },
  { name: "爱因斯坦", sex: "男", category: "国外", note: "物理学家", tg: "己丁壬辛", dz: "卯卯戌亥", vipOnly: true, slug: "aiyinsitan" },
];

export const famousCategories = Array.from(new Set(famousCases.map((item) => item.category)));

/** 可公开收录的案例（非会员内容），用于详情落地页与站点地图。 */
export const publicFamousCases = famousCases.filter((item) => !item.vipOnly);

export function findFamousBySlug(slug: string): FamousCase | undefined {
  return famousCases.find((item) => item.slug === slug);
}

/** 给预渲染脚本用的详情页 SEO 元数据（仅公开案例）。 */
export const famousDetailSeoRoutes = publicFamousCases.map((c) => ({
  route: `famous/${c.slug}`,
  out: `famous/${c.slug}/index.html`,
  title: `${c.name}八字命理 · 四柱干支资料 - 好学文化`,
  desc: `${c.name}（${c.note}）的四柱八字干支：天干${c.tg}、地支${c.dz}。历史人物生辰八字资料，供传统文化对照学习与排盘练习参考，内容仅供学习娱乐。`,
}));
